import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AdminService } from '../../services/admin/admin.service';
import { FileUploadService } from '../../services/file-upload/file-upload.service';
import { CourseDurationModel, CourseModel, CoursePathwayModel, CourseTimeModel, CourseUnitModel, TagModel } from '../../core/models';
import { IErrorMessage } from 'src/app/core/iErrorMessage';
import { clusterNormalTotal, clusterValidationError } from '../../core/cluster-pricing';
import { courseResourceUrl } from '../../core/course-content';
import Quill from 'quill';
import { StateModel } from 'src/app/core/StateModel';
import { CourseDownloadModel } from 'src/app/core/CourseDownloadModel';
import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import { finalize } from 'rxjs/operators';
import { XCourseFilter } from 'src/app/core/common/XCourseFilter';


const SizeStyle: any = Quill.import('attributors/style/size');

SizeStyle.whitelist = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '28px',
  '32px'
];

Quill.register(SizeStyle, true);


@Component({
  selector: 'app-admin-courses',
  templateUrl: './admin-courses.component.html',
  styleUrls: ['./admin-courses.component.scss']
})
export class AdminCoursesComponent implements OnInit {
  courses: CourseModel[] = [];
  tags: TagModel[] = [];
  showDialog = false;
  isLoading = false;
  isSaving = false;
  editor: CourseModel = this.createEmptyCourse();
  selectedTagIds: number[] = [];
  clusterSearch = '';
  catalogueSearch = '';
  catalogueType = 'single';
  visibilityFilter: 'ALL' | 'ACTIVE' | 'INACTIVE' = 'ALL';

  // Same pagination pattern used by Admin Short Courses / Manual Sync.
  currentPage = 1;
  pageSize = 10;
  readonly pageSizeOptions: number[] = [10, 20, 50];

  // ============================================================
  // RICH TEXT EDITOR CONFIG
  // Same configuration used by Admin Short Courses.
  // ============================================================

  editorModules = {
    toolbar: [
      [
        {
          size: [
            '10px',
            '12px',
            '14px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '28px',
            '32px'
          ]
        }
      ],

      ['bold', 'italic', 'underline', 'strike'],

      [
        { header: 1 },
        { header: 2 }
      ],

      [
        { list: 'ordered' },
        { list: 'bullet' }
      ],

      [
        { indent: '-1' },
        { indent: '+1' }
      ],

      [
        { align: [] }
      ],

      // Keep existing Quill colour palette
      [
        { color: [] },
        { background: [] },

        // Custom HEX colour button
        'hexColor'
      ],

      // [
      //   { color: [] },
      //   { background: [] }
      // ],

      ['link'],

      ['clean']
    ],
    handlers: {
      hexColor: function (this: any) {

        const quill = this.quill;

        const currentColor = prompt(
          'Enter text colour HEX value.\nExample: #E31E24'
        );

        if (!currentColor) {
          return;
        }

        let color = currentColor.trim();

        // Automatically add #
        if (!color.startsWith('#')) {
          color = '#' + color;
        }

        // Validate HEX
        const isValidHex =
          /^#([0-9A-F]{3}){1,2}$/i.test(color);

        if (!isValidHex) {
          alert(
            'Invalid HEX colour.\nPlease enter something like #E31E24'
          );
          return;
        }

        quill.format('color', color);
      }
    }
  };
  editorTab: 'details' | 'pricing' | 'content' | 'outcomes' | 'sessions' = 'details';
  private clusterSourceCourse: CourseModel | null = null;

  get filteredAdminCourses(): CourseModel[] {
    const search = this.catalogueSearch.trim().toLowerCase();

    return (this.courses || []).filter(course => {
      const matchesSearch =
        !search ||
        `${course.courseCode || ''} ${course.courseName || ''}`
          .toLowerCase()
          .includes(search);

      const matchesType =
        this.catalogueType === 'all' ||
        (this.catalogueType === 'cluster'
          ? !!course.isCluster
          : !course.isCluster);

      const matchesVisibility =
        this.visibilityFilter === 'ALL' ||
        (this.visibilityFilter === 'ACTIVE' && course.isActive !== false) ||
        (this.visibilityFilter === 'INACTIVE' && course.isActive === false);

      return matchesSearch && matchesType && matchesVisibility;
    });
  }
 // ============================================================
// SERVER-SIDE PAGINATION
// ============================================================

// SQL already returns the requested page.
// No need to use slice().
get paginatedAdminCourses(): CourseModel[] {
  return this.filteredAdminCourses;
}


// Total pages based on database total records.
get totalPages(): number {
  return Math.max(
    1,
    Math.ceil(this.totalRecords / this.pageSize)
  );
}


// Starting record number.
get pageStart(): number {

  if (this.totalRecords === 0) {
    return 0;
  }

  return ((this.currentPage - 1) * this.pageSize) + 1;
}


// Ending record number.
get pageEnd(): number {

  return Math.min(
    this.currentPage * this.pageSize,
    this.totalRecords
  );
}


// Pagination page numbers.
get pageNumbers(): number[] {

  const maxButtons = 5;

  let startPage = Math.max(
    1,
    this.currentPage - 2
  );

  let endPage = Math.min(
    this.totalPages,
    startPage + maxButtons - 1
  );

  startPage = Math.max(
    1,
    endPage - maxButtons + 1
  );

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );
}
onCatalogueSearchChange(): void {
  this.currentPage = 1;
  this.loadCourses();
}

 onCatalogueFilterChange(): void {
  this.currentPage = 1;
  this.loadCourses();
}

  // Change page
changePage(page: number): void {

  if (
    page < 1 ||
    page > this.totalPages ||
    page === this.currentPage ||
    this.isLoading
  ) {
    return;
  }

  this.currentPage = page;

  // Fetch requested records from SQL
  this.loadCourses();
}

  changePageSize(value: string | number): void {

  const newPageSize = Number(value);

  if (!this.pageSizeOptions.includes(newPageSize)) {
    return;
  }

  if (this.pageSize === newPageSize) {
    return;
  }

  this.pageSize = newPageSize;
  this.currentPage = 1;

  // Fetch first page with new size
  this.loadCourses();
}

  getAdminCourseDescription(course: CourseModel): string {
    const source =
      course.shortDescription ||
      course.fullDescription ||
      course.deliveryMode ||
      '';

    const plain = source
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return plain || 'Additional course information can be added later.';
  }

  getAdminCoursePrice(course: CourseModel): string {
    const amount = Number(course.price ?? 0);

    if (amount <= 0) {
      return 'Enquire for price';
    }

    const currency =
      (course.currencyCode || 'AUD').trim().toUpperCase();

    return `${currency} ${amount.toFixed(2)}`;
  }

  clusterLoadError = '';

  /**
   * Complete snapshot of the loaded edit values.
   * Used only as a safeguard so changing the temporary course type cannot
   * accidentally clear arrays/values before Save.
   */
  private editValueSnapshot: CourseModel | null = null;

  // Store the error message list
  errorList: IErrorMessage[] = [];

  /** Course waiting for delete confirmation. */
  pendingDeleteCourse: CourseModel | null = null;

  /** Reusable modal message dialog used instead of browser alert(). */
  showMessageDialog = false;
  messageDialogTitle = '';
  messageDialogText = '';
  messageDialogType: 'success' | 'error' | 'info' = 'info';

  //store states list 
  statesList: StateModel[] = [];

  //store selected state code
  selectedStateCode: string = 'MEL';

  //store form state code
  selectedFormStateCode: string = 'MEL';
 
totalRecords: number = 0;
  constructor(private adminService: AdminService, private uploadService: FileUploadService, private router: Router) { }

  ngOnInit(): void { this.loadPageData(); }

  loadPageData(): void { this.loadCourses(); this.loadTags(); this.loadStates(); }

   loadCourses(): void {

  const filter: XCourseFilter = {
    action: 'ADMIN_LIST',
    type: '',
    search: '',
    searchKey: '',
    isActive: null,
    stateCode: this.selectedStateCode || '',
    pageSize: this.pageSize,
    currentPage: this.currentPage
  };

  this.isLoading = true;

  this.adminService.getCourses(filter).subscribe({

    next: (response) => {

      // Keep both active and inactive courses
      this.courses = response.records || [];

      // Total records from SQL
      this.totalRecords = response.totalRecords || 0;

      // Current page
      this.currentPage = response.currentPage || 1;

      // Handle page becoming invalid after deletion/filtering
      if (
        this.currentPage > this.totalPages &&
        this.totalRecords > 0
      ) {
        this.currentPage = this.totalPages;
        this.loadCourses();
        return;
      }

      this.isLoading = false;
    },

    error: (error) => {

      console.error('Course load failed:', error);

      this.courses = [];
      this.totalRecords = 0;
      this.isLoading = false;
    }
  });
}

  loadTags(): void {
    this.adminService.getTags('ADMIN_LIST').subscribe({
      next: x => this.tags = x || [],
      error: e => { console.error('Tag load failed', e); this.tags = []; }
    });
  }

  openNew(): void {
    this.editValueSnapshot = null;
    this.clusterSourceCourse = null;
    this.editorTab = 'details';
    this.editor = this.createEmptyCourse();
    this.selectedTagIds = [];
    this.errorList = [];
    this.clusterSearch = '';
    this.clusterLoadError = '';
    this.showDialog = true;
  }

  loadStates(): void {

    this.adminService.getStatesService('ALL_ACTIVE').subscribe({
      next: (response: StateModel[]) => {
        this.statesList = response || [];
      },
      error: error => {
        console.error('Failed to load states:', error);
        this.statesList = [];
      }
    });

  }

  createClusterForCourse(course: CourseModel): void {
    if (course.isCluster || !course.courseId) return;
    this.openNew();
    this.clusterSourceCourse = { ...course };
    this.editor = {
      ...this.createEmptyCourse(), isCluster: true,
      courseName: course.courseName + ' cluster', currencyCode: course.currencyCode,
      clusterAnchorCourseId: course.courseId, clusterCourseIds: [course.courseId]
    };
    this.selectedTagIds = this.getSelectedTagIds(course);
    this.editorTab = 'pricing';
  }

  /**
   * Changes the course type in-place while preserving every value already loaded
   * into the editor.
   *
   * Edit behaviour:
   * - Individual -> Cluster keeps the SAME CourseId and all normal-course values.
   * - Cluster -> Individual keeps the SAME CourseId and all cluster/course values.
   * - Toggling repeatedly before Save never rebuilds the editor and never creates
   *   a second course record.
   * - Cluster membership is only committed/removed when Save is clicked.
   */
  setCourseType(isCluster: boolean): void {
    if (!!this.editor.isCluster === isCluster) {
      return;
    }

    // Never call openNew()/createClusterForCourse() from the edit toggle.
    // Recreating the editor here was the reason the existing course values and
    // CourseId disappeared when an individual course was changed to a cluster.
    this.editor.isCluster = isCluster;

    if (this.editValueSnapshot) {
      this.editor.courseTimes = this.editor.courseTimes ?? this.editValueSnapshot.courseTimes?.map(x => ({ ...x })) ?? [];
      this.editor.courseUnits = this.editor.courseUnits ?? this.editValueSnapshot.courseUnits?.map(x => ({ ...x })) ?? [];
      this.editor.deliveryOptions = this.editor.deliveryOptions ?? [...(this.editValueSnapshot.deliveryOptions || [])];
      this.editor.durationOptions = this.editor.durationOptions ?? this.editValueSnapshot.durationOptions?.map(x => ({ ...x })) ?? [];
      this.editor.coursePrerequisites = this.editor.coursePrerequisites ?? [...(this.editValueSnapshot.coursePrerequisites || [])];
      this.editor.employmentOutcomes = this.editor.employmentOutcomes ?? [...(this.editValueSnapshot.employmentOutcomes || [])];
      this.editor.studyPathways = this.editor.studyPathways ?? this.editValueSnapshot.studyPathways?.map(x => ({ ...x })) ?? [];
    }

    if (isCluster) {
      // The current course is always the fixed/required course in an in-place
      // conversion. It is shown selected + disabled in the UI, but is NOT stored
      // as a self-reference in ECB_CourseClusterUnit.
      if (this.editor.courseId) {
        this.editor.clusterAnchorCourseId = this.editor.courseId;
        this.editor.clusterCourseIds = (this.editor.clusterCourseIds || [])
          .filter(id => Number(id) !== Number(this.editor.courseId));
      }

      return;
    }

    // Do not clear clusterCourseIds, sessions, delivery options, durations,
    // categories, outcomes, units, pathways, or other values here. Keeping them
    // in memory allows Cluster -> Individual -> Cluster before Save with no loss.
    //
    // If Individual is ultimately saved, ECB_Course_SET removes the database
    // cluster membership for this same CourseId.
  }

  selectClusterAnchor(id: number): void {
    if (this.editor.courseId || this.clusterSourceCourse) return;
    const course = this.courses.find(value => value.courseId === Number(id) && !value.isCluster);
    if (!course) return;
    this.editor.clusterAnchorCourseId = course.courseId;
    this.editor.clusterCourseIds = [...new Set([course.courseId, ...(this.editor.clusterCourseIds || [])])];
  }

  edit(course: CourseModel): void {
    this.clusterLoadError = '';
    const detailRequest = course.isCluster ? this.adminService.getClusterDetail(course.courseId) : this.adminService.getCourseDetail(course.courseId);
    detailRequest.subscribe({
      next: detail => {
        if (detail?.courseId === course.courseId) this.openEditDialog(detail);
        else this.clusterLoadError = 'Unable to load the full course details. Please try again.';
      },
      error: () => {
        this.clusterLoadError = 'Unable to load the full course details. Please try again.';
      }
    });
  }

  openEditDialog(course: CourseModel): void {

    this.selectedStateCode = course.stateCode;
    this.clusterSourceCourse = null;
    this.editorTab = 'details';
    this.editor = {
      ...this.createEmptyCourse(),
      ...course,
      startDate: this.formatDateForInput(course.startDate),
      // Only active, meaningful sessions belong in the editor. Older versions
      // soft-deactivated sessions, which made deleted/blank rows reappear on edit.
      courseTimes: (course.courseTimes || [])
        .filter(time => time.isActive !== false && this.isMeaningfulCourseTime(time))
        .map(time => this.normalizeCourseTime(time, course.courseId))
    };
    this.editor.courseUnits = (course.courseUnits || []).map((unit, index) => ({ ...unit, code: unit.code || '', unitType: unit.unitType || 'core', sortOrder: unit.sortOrder ?? index }));
    this.editor.employmentOutcomes = [...(course.employmentOutcomes || [])];
    this.editor.deliveryOptions = [...(course.deliveryOptions || [])];
    this.editor.coursePrerequisites = [...(course.coursePrerequisites || [])];
    this.editor.durationOptions = (course.durationOptions || []).map(duration => ({ ...duration }));
    this.editor.studyPathways = (course.studyPathways || []).map(pathway => ({ ...pathway }));
    this.editor.clusterCourseIds = [...(course.clusterCourseIds || course.clusterCourses?.map(unit => unit.courseId) || [])];
    this.editor.clusterCourses = (course.clusterCourses || []).map(unit => ({ ...unit }));
    if (course.isCluster) {
      // For an in-place cluster, the current CourseId is the required visual
      // anchor. Persisted clusterCourseIds contain only the additional courses.
      this.editor.clusterAnchorCourseId = course.courseId;
      this.editor.clusterCourseIds = (this.editor.clusterCourseIds || [])
        .filter(id => Number(id) !== Number(course.courseId));
    }
    this.errorList = [];
    this.clusterSearch = '';
    this.selectedTagIds = this.getSelectedTagIds(course);

    this.editValueSnapshot = {
      ...this.editor,
      courseTimes: (this.editor.courseTimes || []).map(x => ({ ...x })),
      courseUnits: (this.editor.courseUnits || []).map(x => ({ ...x })),
      deliveryOptions: [...(this.editor.deliveryOptions || [])],
      durationOptions: (this.editor.durationOptions || []).map(x => ({ ...x })),
      coursePrerequisites: [...(this.editor.coursePrerequisites || [])],
      employmentOutcomes: [...(this.editor.employmentOutcomes || [])],
      studyPathways: (this.editor.studyPathways || []).map(x => ({ ...x })),
      clusterCourseIds: [...(this.editor.clusterCourseIds || [])],
      clusterCourses: (this.editor.clusterCourses || []).map(x => ({ ...x }))
    };

    this.showDialog = true;
  }
  /* 
    save(): void {
      if (!this.ValidateFields()) {
        return;
      }
  
      this.isSaving = true;
      const selectedTags = this.tags.filter(tag => this.selectedTagIds.includes(Number(tag.tagId)));
      const saveModel: CourseModel = {
        ...this.editor,
        tags: selectedTags,
        tagIds: [...this.selectedTagIds],
        courseTimes: (this.editor.courseTimes || []).filter(x => x.dateRange || x.startDate || x.time || x.locationName).map(x => this.normalizeCourseTime(x, this.editor.courseId))
      };
      this.adminService.saveCourse(saveModel).subscribe({
        next: () => { this.isSaving = false; this.showDialog = false; this.loadCourses(); },
        error: e => {
          this.isSaving = false;
          console.error('Course save failed', e);
          this.showMessage(
            'Unable to save course',
            e?.error?.message || 'Course save failed. Please check the entered values and try again.',
            'error'
          );
        }
      });
    } */


  save(): void {
    if (this.isSaving) return;
    if (!this.ValidateFields()) {
      return;
    }


    this.isSaving = true;

    const selectedTags = this.tags.filter(tag =>
      this.selectedTagIds.includes(Number(tag.tagId))
    );

    // Because SQL deletes all old units and inserts them again,
    // send every unit as a new unit with id = 0.
    const courseUnits: CourseUnitModel[] =
      (this.editor.courseUnits || [])
        .filter(unit => unit.name && unit.name.trim())
        .map((unit, index) => ({
          id: 0,
          fkCourseId: this.editor.courseId || 0,
          name: unit.name.trim(),
          code: (unit.code || '').trim(),
          unitType: unit.unitType || 'core',
          sortOrder: index,
          isActive: unit.isActive !== false
        }));

    const saveModel: CourseModel = {
      ...this.editor,

      // ASP.NET expects Nullable<DateTime> for StartDate.
      // Never send an empty string ("") for an optional date.
      startDate:
        (this.editor.startDate && this.editor.startDate !== ('' as any))
          ? this.formatDateForInput(this.editor.startDate)
          : new Date(),

      // Course code is immutable after creation. The database enforces this too.
      courseCode: this.editor.courseId
        ? (this.editValueSnapshot?.courseCode || this.editor.courseCode || '').trim()
        : (this.editor.courseCode || '').trim(),
      isCluster: !!this.editor.isCluster,
      clusterCourseIds: this.editor.isCluster ? [...(this.editor.clusterCourseIds || [])] : [],
      clusterAnchorCourseId: this.editor.isCluster ? this.editor.clusterAnchorCourseId : undefined,
      clusterCourses: undefined,
      employmentOutcomes: (this.editor.employmentOutcomes || []).map(outcome => outcome.trim()).filter(Boolean),

      // Pathways are selected from existing courses. The API/SQL resolves the
      // selected course code/name from RelatedCourseId, so no free-text URL is saved.
      studyPathways: (this.editor.studyPathways || [])
        .filter(pathway => Number(pathway.relatedCourseId) > 0)
        .map(pathway => ({
          relatedCourseId: Number(pathway.relatedCourseId),
          courseCode: (pathway.courseCode || '').trim(),
          courseName: (pathway.courseName || '').trim()
        })),

      fundingInformation: (this.editor.fundingInformation || '').trim(),
      qualificationType: (this.editor.qualificationType || '').trim(),

      // Delivery mode is managed as a list. Keep the legacy summary field populated
      // because sessions/catalogue cards elsewhere in the existing application use it.
      deliveryOptions: (this.editor.deliveryOptions || []).map(value => value.trim()).filter(Boolean),
      deliveryMode: (this.editor.deliveryOptions || []).map(value => value.trim()).filter(Boolean).join(' / '),

      // Duration is managed as structured list rows (label + value).
      durationOptions: (this.editor.durationOptions || [])
        .map(item => ({
          label: (item.label || '').trim(),
          duration: (item.duration || '').trim()
        }))
        .filter(item => item.label && item.duration),

      placementHours: this.editor.placementHours == null || this.editor.placementHours === ('' as any) ? null : Number(this.editor.placementHours),
      coursePrerequisites: (this.editor.coursePrerequisites || []).map(value => value.trim()).filter(Boolean),
      pathwaysIntroduction: (this.editor.pathwaysIntroduction || '').trim(),
      pathwaysNote: (this.editor.pathwaysNote || '').trim(),

      tags: selectedTags,

      tagIds: [...this.selectedTagIds],
      stateCode: this.selectedFormStateCode,

      // Preserve valid sessions regardless of the temporary course type.
      // This is important for in-place Individual <-> Cluster conversion: toggling
      // or saving as a cluster must not destroy the sessions that belong to the
      // same CourseId. Blank/deleted rows are still removed.
      courseTimes: (this.editor.courseTimes || [])
        .filter(time => time.isActive !== false && this.isMeaningfulCourseTime(time))
        .map(time => this.normalizeCourseTime(time, this.editor.courseId)),


      courseUnits
    };



    const saveRequest = this.editor.isCluster ? this.adminService.saveCluster(saveModel) : this.adminService.saveCourse(saveModel);
    saveRequest.subscribe({
      next: () => {
        this.isSaving = false;
        this.showDialog = false;
        this.loadCourses();
      },

      error: error => {
        this.isSaving = false;
        console.error('Course save failed', error);

        const serverMessage = error?.error?.message || error?.error || 'Unable to save. Please try again.';
        const isDuplicateCode = typeof serverMessage === 'string' &&
          serverMessage.toLowerCase().includes('course code') &&
          serverMessage.toLowerCase().includes('already exists');

        this.errorList = [{
          ErrorCode: isDuplicateCode ? 'CODE_DUPLICATE' : 'SAVE_FAILED',
          ErrorMessage: typeof serverMessage === 'string' ? serverMessage : 'Unable to save. Please try again.'
        }];
      }
    });
  }


  /**
   * Returns true when another Long Course already uses the supplied course code.
   * Course codes are compared case-insensitively and with surrounding spaces removed.
   */
  private courseCodeAlreadyExists(courseCode: string): boolean {
    const normalizedCode = (courseCode || '').trim().toLowerCase();

    if (!normalizedCode) {
      return false;
    }

    return (this.courses || []).some(course =>
      Number(course.courseId) !== Number(this.editor.courseId || 0) &&
      (course.courseCode || '').trim().toLowerCase() === normalizedCode
    );
  }

  // Validate the fields.
  // Only Course code, Qualification type, Course name and Course categories
  // are mandatory. Every other Long Course field can be completed later.
  ValidateFields(): boolean {
    this.errorList = [];

    const normalizedCourseCode =
      (this.editor.courseCode || '').trim();

    if (!normalizedCourseCode) {
      this.errorList.push({
        ErrorCode: 'CODE_EMPTY',
        ErrorMessage: 'Course code is mandatory.'
      });
    } else if (
      !this.editor.courseId &&
      this.courseCodeAlreadyExists(normalizedCourseCode)
    ) {
      this.errorList.push({
        ErrorCode: 'CODE_DUPLICATE',
        ErrorMessage:
          `Course code "${normalizedCourseCode}" already exists. Please enter a unique course code.`
      });
    }

    if (!(this.editor.qualificationType || '').trim()) {
      this.errorList.push({
        ErrorCode: 'QUALIFICATION_REQUIRED',
        ErrorMessage: 'Qualification type is mandatory.'
      });
    }

    if (!(this.editor.courseName || '').trim()) {
      this.errorList.push({
        ErrorCode: 'NAME_EMPTY',
        ErrorMessage: 'Course name is mandatory.'
      });
    }

    if (!(this.selectedTagIds && this.selectedTagIds.length > 0)) {
      this.errorList.push({
        ErrorCode: 'TAGS_EMPTY',
        ErrorMessage: 'Select at least one course qualification.'
      });
    }

    // Optional values are validated only when supplied.
    if (
      this.editor.price != null &&
      Number(this.editor.price) < 0
    ) {
      this.errorList.push({
        ErrorCode: 'PRICE_INVALID',
        ErrorMessage: 'Course price cannot be negative.'
      });
    }

    if (
      this.editor.placementHours != null &&
      this.editor.placementHours !== ('' as any) &&
      Number(this.editor.placementHours) < 0
    ) {
      this.errorList.push({
        ErrorCode: 'PLACEMENT_INVALID',
        ErrorMessage: 'Placement hours cannot be negative.'
      });
    }

    if (this.editor.isCluster && this.clusterError) {
      this.errorList.push({
        ErrorCode: 'CLUSTER_INVALID',
        ErrorMessage: this.clusterError
      });
    }

    const pathwayIds =
      (this.editor.studyPathways || [])
        .map(pathway => Number(pathway.relatedCourseId))
        .filter(id => id > 0);

    if (new Set(pathwayIds).size !== pathwayIds.length) {
      this.errorList.push({
        ErrorCode: 'PATHWAY_DUPLICATE',
        ErrorMessage:
          'The same further-study course cannot be selected more than once.'
      });
    }

    if (
      (this.editor.durationOptions || [])
        .some(item =>
          (!!item.label?.trim() || !!item.duration?.trim()) &&
          (!item.label?.trim() || !item.duration?.trim())
        )
    ) {
      this.errorList.push({
        ErrorCode: 'DURATION_EMPTY',
        ErrorMessage:
          'For an optional duration row, enter both the label and duration or remove the row.'
      });
    }

    if (
      !!this.editor.brochureUrl?.trim() &&
      !courseResourceUrl(this.editor.brochureUrl)
    ) {
      this.errorList.push({
        ErrorCode: 'CONTENT_URL',
        ErrorMessage:
          'Use a valid HTTP(S) or website-relative brochure link.'
      });
    }

    if (
      (this.editor.courseUnits || [])
        .some(unit =>
          !!(unit.name?.trim() || unit.code?.trim()) &&
          !!unit.unitType &&
          !['core', 'elective'].includes(unit.unitType)
        )
    ) {
      this.errorList.push({
        ErrorCode: 'UNIT_TYPE',
        ErrorMessage:
          'Each entered study unit must be Core or Elective.'
      });
    }

    return this.errorList.length === 0;
  }

  // Check if the error exists
  CheckErrorCode(errorCode: string) {
    // Find for the code
    let indexObject = this.errorList.findIndex(obj => obj.ErrorCode == errorCode);
    // Return the error object
    if (indexObject < 0) {
      return null;
    } else {
      return this.errorList[indexObject];
    }
  }


  /**
   * Opens a consistent PrimeNG message dialog.
   * Browser alert() is intentionally not used anywhere in this component.
   */
  private showMessage(
    title: string,
    message: string,
    type: 'success' | 'error' | 'info' = 'info'
  ): void {
    this.messageDialogTitle = title;
    this.messageDialogText = message;
    this.messageDialogType = type;
    this.showMessageDialog = true;
  }

  /** Opens the delete confirmation dialog without mutating the course. */
  requestDelete(course: CourseModel): void {
    this.pendingDeleteCourse = course;
  }

  /** Closes the delete dialog without deleting anything. */
  cancelDelete(): void {
    this.pendingDeleteCourse = null;
  }

  /** Confirms deletion and immediately removes the row from the admin list. */
  confirmDelete(): void {
    const course = this.pendingDeleteCourse;

    if (!course?.courseId) {
      this.pendingDeleteCourse = null;
      return;
    }

    const courseId = course.courseId;
    this.pendingDeleteCourse = null;

    this.adminService.deleteCourse(courseId).subscribe({
      next: () => {

  this.showMessage(
    'Course deleted',
    `${course.courseCode ? course.courseCode + ' - ' : ''}${course.courseName} has been removed from the admin list.`,
    'success'
  );

  // Refresh records and total count from SQL
  this.loadCourses();
},
      error: error => {
        console.error('Course delete failed', error);
        this.showMessage(
          'Unable to delete course',
          error?.error?.message || 'The course could not be deleted. Please try again.',
          'error'
        );
      }
    });
  }

  /**
   * Compatibility wrapper for older template calls.
   * New templates call requestDelete(course) so the confirmation can display the
   * course code and name.
   */
  delete(courseId: number): void {
    const course = this.courses.find(item => item.courseId === courseId);
    if (course) {
      this.requestDelete(course);
    }
  }

  uploadImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.uploadService.uploadSingleFile(input.files[0], 'course-images').subscribe({
      next: x => this.editor.imageUrl = x?.publicUrl || '',
      error: e => {
        console.error('Image upload failed', e);
        this.showMessage(
          'Image upload failed',
          e?.error?.message || 'The course image could not be uploaded. Please try again.',
          'error'
        );
      }
    });
  }

  // Handle course image upload error
  onCourseImageUploadError(message: string): void {

    this.showMessage(
      'Image upload failed',
      message || 'The course image could not be uploaded. Please try again.',
      'error'
    );
  }

  uploadBrochure(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.uploadService.uploadSingleFile(input.files[0], 'course-brochure').subscribe({
      next: x => this.editor.brochureUrl = x?.publicUrl || '',
      error: e => {
        console.error('Brochure upload failed', e);
        this.showMessage(
          'Brochure upload failed',
          e?.error?.message || 'The brochure could not be uploaded. Please try again.',
          'error'
        );
      }
    });
  }


  /** Add a structured delivery option shown in the public summary card. */
  addDeliveryOption(): void { (this.editor.deliveryOptions ||= []).push(''); }
  removeDeliveryOption(index: number): void { this.editor.deliveryOptions?.splice(index, 1); }

  /** Add one labelled duration row, e.g. Classroom / 9 - 12 months. */
  addDurationOption(): void {
    (this.editor.durationOptions ||= []).push({ label: '', duration: '' });
  }

  removeDurationOption(index: number): void {
    this.editor.durationOptions?.splice(index, 1);
  }

  /** Add a prerequisite. The public popup hides the prerequisite panel when this list is empty. */
  addPrerequisite(): void { (this.editor.coursePrerequisites ||= []).push(''); }
  removePrerequisite(index: number): void { this.editor.coursePrerequisites?.splice(index, 1); }

  addOutcome(): void { (this.editor.employmentOutcomes ||= []).push(''); }
  removeOutcome(index: number): void { this.editor.employmentOutcomes.splice(index, 1); }
  /** Add one further-study row. Admin then selects an existing course. */
  addPathway(): void {
    (this.editor.studyPathways ||= []).push({ relatedCourseId: 0, courseCode: '', courseName: '' });
  }

  /** Keep display code/name in sync immediately when a pathway course is selected. */
  onPathwayCourseSelected(pathway: CoursePathwayModel, courseId: number): void {
    const selected = this.courses.find(course => course.courseId === Number(courseId));
    pathway.relatedCourseId = Number(courseId || 0);
    pathway.courseCode = selected?.courseCode || '';
    pathway.courseName = selected?.courseName || '';
  }

  /** Existing individual courses that can be used as further-study links. */
  get pathwayCourseCandidates(): CourseModel[] {
    return this.courses
      .filter(course =>
        !course.isCluster &&
        course.isActive &&
        course.courseId !== this.editor.courseId
      )
      .sort((a, b) => (a.courseCode + a.courseName).localeCompare(b.courseCode + b.courseName));
  }
  removePathway(index: number): void { this.editor.studyPathways.splice(index, 1); }
  trackByIndex(index: number): number { return index; }
  moveStudyUnit(index: number, direction: number): void {
    const units = this.editor.courseUnits || [];
    const target = index + direction;
    if (target < 0 || target >= units.length) return;
    [units[index], units[target]] = [units[target], units[index]];
    units.forEach((unit, order) => unit.sortOrder = order);
  }



  addCourseTime(): void {
    if (!this.editor.courseTimes) this.editor.courseTimes = [];
    this.editor.courseTimes.push({
      courseTimeId: 0,
      courseId: this.editor.courseId,
      dateRange: '',
      startDate: '',
      endDate: '',
      time: '8:30 AM - 4:30 PM',
      locationName: this.editor.locationName || '',
      deliveryMode: this.editor.deliveryOptions?.[0] || this.editor.deliveryMode || 'Face to Face',
      seatsAvailable: 10,
      isActive: true
    });
  }

  removeCourseTime(index: number): void {
    this.editor.courseTimes = (this.editor.courseTimes || []).filter((_, i) => i !== index);
  }

  syncDateRange(time: CourseTimeModel): void {
    const start = this.formatDateForInput(time.startDate);
    const end = this.formatDateForInput(time.endDate);
    time.dateRange = start && end && start !== end ? `${start} - ${end}` : start;
  }

  toggleTag(tagId: number, checked: boolean): void {
    const id = Number(tagId);
    if (checked && !this.selectedTagIds.includes(id)) this.selectedTagIds.push(id);
    if (!checked) this.selectedTagIds = this.selectedTagIds.filter(x => x !== id);
  }

  isTagSelected(tagId: number): boolean { return this.selectedTagIds.includes(Number(tagId)); }

  goToTags(): void { this.showDialog = false; this.router.navigate(['/admin/tags']); }

  splitTagNames(tagNames: string | null | undefined): string[] { return tagNames ? tagNames.split(',').map(x => x.trim()).filter(Boolean) : []; }

  private createEmptyCourse(): CourseModel {
    return {
      courseId: 0,
      courseCode: '',
      courseName: '',
      shortDescription: '',
      fullDescription: '',
      price: 0,
      currencyCode: 'AUD',
      startDate: null, deliveryMode: '', locationName: '', imageUrl: '', brochureUrl: '', isActive: true, tags: [], tagIds: [], tagNames: '', courseTimes: [], courseUnits: [], isCluster: false, clusterCourseIds: [], clusterCourses: [], employmentOutcomes: [], studyPathways: [], fundingInformation: '', qualificationType: '', deliveryOptions: [], durationOptions: [], placementHours: null, coursePrerequisites: [], pathwaysIntroduction: '', pathwaysNote: ''
    };
  }

  get clusterCandidates(): CourseModel[] {
    const search = this.clusterSearch.trim().toLowerCase();
    return this.allClusterCandidates.filter(course => this.isClusterAnchor(course.courseId) ||
      !search || `${course.courseCode} ${course.courseName} ${this.courseCategoryNames(course).join(' ')}`.toLowerCase().includes(search))
      .sort((a, b) => Number(this.isClusterAnchor(b.courseId)) - Number(this.isClusterAnchor(a.courseId)));
  }

  get allClusterCandidates(): CourseModel[] {
    const courses = [...this.courses];

    if (this.clusterSourceCourse &&
      !courses.some(course => course.courseId === this.clusterSourceCourse?.courseId)) {
      courses.push(this.clusterSourceCourse);
    }

    // Keep the current course in the candidate collection so it can be displayed
    // as selected + locked. Other existing clusters are not valid linked courses.
    return courses.filter(course =>
      course.courseId === this.editor.courseId || !course.isCluster
    );
  }

  get canChooseClusterAnchor(): boolean { return !this.editor.courseId && !this.clusterSourceCourse; }
  isClusterAnchor(id: number): boolean {
    if (this.editor.courseId) {
      return Number(id) === Number(this.editor.courseId);
    }

    return Number(id) === Number(this.editor.clusterAnchorCourseId);
  }

  courseCategoryNames(course: CourseModel): string[] {
    const names = course.tags?.map(tag => tag.tagName).filter(Boolean) || [];
    if (!names.length) names.push(...this.splitTagNames(course.tagNames));
    if (!names.length) names.push(...this.tags.filter(tag => course.tagIds?.includes(tag.tagId)).map(tag => tag.tagName));
    return names.length ? [...new Set(names)] : ['Uncategorised'];
  }

  /**
  * Builds the cluster pricing preview from:
  * 1. Current course at its ORIGINAL individual price
  * 2. All additional linked courses selected by the user
  *
  * editor.price is the new discounted cluster price, so we must NOT use it
  * as the current course's normal individual price.
  */
  get clusterPreview(): CourseModel {
    const anchorId = Number(
      this.editor.clusterAnchorCourseId ||
      this.editor.courseId ||
      this.clusterSourceCourse?.courseId ||
      0
    );

    /*
     * Additional selected courses only.
     * The current/anchor course is added separately below.
     */
    const additionalIds = (this.editor.clusterCourseIds || [])
      .map(Number)
      .filter(id => id > 0 && id !== anchorId);

    let anchorCourse: CourseModel | undefined;

    /*
     * Editing an existing course and converting it to a cluster.
     *
     * IMPORTANT:
     * editor.price can now be changed to the discounted cluster price.
     * Therefore use editValueSnapshot.price for the original/current
     * individual course price.
     */
    if (
      anchorId > 0 &&
      Number(this.editor.courseId) === anchorId &&
      this.editValueSnapshot
    ) {
      anchorCourse = {
        ...this.editValueSnapshot,
        courseId: anchorId,
        price: Number(this.editValueSnapshot.price || 0)
      };
    }

    /*
     * Add Discount flow from the course listing.
     */
    else if (
      anchorId > 0 &&
      this.clusterSourceCourse &&
      Number(this.clusterSourceCourse.courseId) === anchorId
    ) {
      anchorCourse = {
        ...this.clusterSourceCourse
      };
    }

    /*
     * New cluster where the anchor/current course was selected manually.
     */
    else if (anchorId > 0) {
      const candidate = this.allClusterCandidates.find(
        course => Number(course.courseId) === anchorId
      );

      if (candidate) {
        anchorCourse = {
          ...candidate
        };
      }
    }

    /*
     * Resolve all additionally selected courses.
     */
    const additionalCourses = additionalIds
      .map(id =>
        this.allClusterCandidates.find(
          course => Number(course.courseId) === id
        )
      )
      .filter((course): course is CourseModel => !!course)
      .map(course => ({
        ...course
      }));

    return {
      ...this.editor,

      /*
       * Pricing calculation receives the COMPLETE cluster:
       *
       * Current course
       * +
       * Additional selected courses
       */
      clusterCourses: [
        ...(anchorCourse ? [anchorCourse] : []),
        ...additionalCourses
      ]
    };
  }

  get clusterError(): string {
    const anchorId = Number(
      this.editor.clusterAnchorCourseId ||
      this.editor.courseId ||
      this.clusterSourceCourse?.courseId ||
      0
    );

    if (!anchorId || !this.isClusterUnitSelected(anchorId)) {
      return 'The current course must remain included in the cluster.';
    }

    const additionalIds = (this.editor.clusterCourseIds || [])
      .map(Number)
      .filter(id => id > 0 && id !== anchorId);

    // Minimum:
    // current course + one additional linked course.
    if (additionalIds.length < 1) {
      return 'Select at least one more course. The minimum cluster is the current course plus one linked course.';
    }

    /*
     * Pricing preview contains:
     *
     * current course
     * +
     * additional selected courses
     */
    const previewCourses = this.clusterPreview.clusterCourses || [];

    const expectedCount = 1 + additionalIds.length;

    if (previewCourses.length !== expectedCount) {
      return 'One or more selected courses could not be loaded. Please clear the additional courses and select them again.';
    }

    /*
     * Make sure every selected additional course exists in the preview.
     */
    const previewIds = new Set(
      previewCourses.map(course => Number(course.courseId))
    );

    const missingAdditionalCourse = additionalIds.some(
      id => !previewIds.has(id)
    );

    if (missingAdditionalCourse) {
      return 'One or more selected courses could not be loaded. Please clear the additional courses and select them again.';
    }

    /*
     * IMPORTANT:
     *
     * clusterPreview intentionally contains the current course because it is
     * required when calculating the NORMAL TOTAL.
     *
     * clusterValidationError() normally rejects a course whose ID matches the
     * cluster master because persisted ECB_CourseClusterUnit rows cannot contain
     * self references.
     *
     * For UI pricing validation only, use courseId = 0 so the full pricing
     * composition can be validated without treating the current course as an
     * invalid database self-reference.
     *
     * save() still sends ONLY additional clusterCourseIds to the API/database.
     */
    const pricingValidationModel: CourseModel = {
      ...this.clusterPreview,
      courseId: 0
    };

    return clusterValidationError(pricingValidationModel);
  }

  get clusterNormalPrice(): number { return clusterNormalTotal(this.clusterPreview); }

  isClusterUnitSelected(id: number): boolean {
    return this.isClusterAnchor(id) || (this.editor.clusterCourseIds || []).includes(id);
  }

  toggleClusterUnit(id: number, checked: boolean): void {
    if (this.isClusterAnchor(id)) return;
    const ids = this.editor.clusterCourseIds || [];
    this.editor.clusterCourseIds = checked ? [...new Set([...ids, id])] : ids.filter(value => value !== id);
  }

  clearClusterSelection(): void {
    // Existing edited course stays visually selected through clusterAnchorCourseId.
    // Only additional persisted linked-course IDs are cleared.
    this.editor.clusterCourseIds =
      !this.editor.courseId && this.editor.clusterAnchorCourseId
        ? [this.editor.clusterAnchorCourseId]
        : [];
  }

  private getSelectedTagIds(course: CourseModel): number[] {
    if (course.tagIds?.length) return course.tagIds.map(Number);
    if (course.tags?.length) return course.tags.filter(x => Number(x.tagId) > 0).map(x => Number(x.tagId));
    return [];
  }

  /** Returns true only for a real session row that has a date/date range. */
  private isMeaningfulCourseTime(time: CourseTimeModel | null | undefined): boolean {
    if (!time) return false;
    return !!(
      this.formatDateForInput(time.startDate) ||
      this.formatDateForInput(time.endDate) ||
      (time.dateRange || '').trim()
    );
  }

  private normalizeCourseTime(time: CourseTimeModel, courseId: number): CourseTimeModel {
    return { courseTimeId: Number(time.courseTimeId || 0), courseId: Number(time.courseId || courseId || 0), dateRange: time.dateRange || '', startDate: this.formatDateForInput(time.startDate), endDate: this.formatDateForInput(time.endDate), time: time.time || '', locationName: time.locationName || '', deliveryMode: time.deliveryMode || '', seatsAvailable: Number(time.seatsAvailable || 0), isActive: time.isActive !== false };
  }

  private formatDateForInput(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && dateValue.length >= 10) return dateValue.substring(0, 10);
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  addCourseUnit(): void {
    // Initialize the array when it is missing.
    if (!this.editor.courseUnits) {
      this.editor.courseUnits = [];
    }

    // Add a new empty course unit row.
    this.editor.courseUnits.push({
      id: 0,
      fkCourseId: this.editor.courseId || 0,
      name: '',
      isActive: true,
      code: '', unitType: 'core', sortOrder: this.editor.courseUnits.length
    });
  }

  removeCourseUnit(index: number): void {
    // Check that the array and index are valid.
    if (
      !this.editor.courseUnits ||
      index < 0 ||
      index >= this.editor.courseUnits.length
    ) {
      return;
    }

    // Remove the selected unit from the frontend list.
    this.editor.courseUnits.splice(index, 1);
  }

  clearCourseUnits(): void {
    this.editor.courseUnits = [];
  }


onStateChange(stateCode: string): void {

  this.selectedStateCode = stateCode;
  this.selectedFormStateCode = stateCode;

  this.currentPage = 1;

  this.loadCourses();
}



  /* ============================================
     COURSE DOWNLOAD VARIABLES
  ============================================ */

  showDownloadDialog: boolean = false;

  downloadCourseType: 'ALL' | 'SHORT' | 'LONG' = 'ALL';

  downloadShortDescription: boolean = true;

  downloadDescription: boolean = true;

  isDownloading: boolean = false;


  /* ============================================
     OPEN DOWNLOAD POPUP
  ============================================ */

  openDownloadDialog(): void {

    this.downloadCourseType = 'ALL';

    this.downloadShortDescription = true;

    this.downloadDescription = true;

    this.showDownloadDialog = true;
  }


  /* ============================================
     DOWNLOAD COURSES AS EXCEL
  ============================================ */

  downloadCourseExcel(): void {

    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;

    this.adminService.getCourseDownload(
      this.downloadCourseType,
      this.downloadShortDescription,
      this.downloadDescription
    )
      .pipe(
        finalize(() => {
          this.isDownloading = false;
        })
      )
      .subscribe({

        next: async (courses: CourseDownloadModel[]) => {

          try {

            if (!courses || courses.length === 0) {
              alert('No courses found to download.');
              return;
            }

            await this.generateCourseExcel(courses);

            this.showDownloadDialog = false;

          } catch (error) {

            console.error('Excel generation failed:', error);

            alert('Unable to generate Excel file.');
          }

        },

        error: (error) => {

          console.error('Course download failed:', error);

          alert('Unable to retrieve courses.');

        }

      });

  }


  /* ============================================
     GENERATE EXCEL FILE
  ============================================ */


  private async generateCourseExcel(
    courses: CourseDownloadModel[]
  ): Promise<void> {

    // Create workbook
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'ECB Training';
    workbook.created = new Date();

    /* ============================================
       CREATE WORKSHEET
    ============================================ */

    const createWorksheet = (
      sheetName: string,
      courseList: CourseDownloadModel[]
    ): void => {

      const worksheet = workbook.addWorksheet(sheetName);

      /* ============================================
         DYNAMIC COLUMNS
      ============================================ */

      const columns: any[] = [

        {
          header: 'Course Code',
          key: 'courseCode',
          width: 22
        },

        {
          header: 'Course Title',
          key: 'courseTitle',
          width: 45
        }

      ];

      // Include short description
      if (this.downloadShortDescription) {

        columns.push({
          header: 'Short Description',
          key: 'shortDescription',
          width: 55
        });

      }

      // Include full description
      if (this.downloadDescription) {

        columns.push({
          header: 'Full Description',
          key: 'description',
          width: 70
        });

      }

      // Price column
      columns.push({
        header: 'Price',
        key: 'price',
        width: 18
      });

      // Course type column
      columns.push({
        header: 'Course Type',
        key: 'courseType',
        width: 18
      });

      // Course category column
      columns.push({
        header: 'Course Category',
        key: 'courseCategory',
        width: 22
      });

      worksheet.columns = columns;

      /* ============================================
         ADD COURSE DATA
      ============================================ */

      courseList.forEach((course) => {

        const rowData: any = {

          courseCode: course.courseCode || '',

          courseTitle: course.courseTitle || '',

          price: course.price ?? 0,

          courseType: course.courseType === 'LONG'
            ? 'Qualification'
            : 'Short Course',

          courseCategory: course.courseCategory || (
            course.courseType === 'LONG'
              ? 'QUALIFICATION'
              : 'UOC'
          )

        };

        if (this.downloadShortDescription) {

          rowData.shortDescription =
            this.getPlainExcelText(course.shortDescription);

        }

        if (this.downloadDescription) {

          rowData.description =
            this.getPlainExcelText(course.description);

        }

        worksheet.addRow(rowData);

      });

      /* ============================================
         HEADER STYLE - DARK ORANGE
      ============================================ */

      const headerRow = worksheet.getRow(1);

      headerRow.height = 30;

      headerRow.eachCell((cell) => {

        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' },
          size: 11
        };

        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF8C00' }
        };

        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center'
        };

        cell.border = {
          bottom: {
            style: 'thin',
            color: { argb: 'FFE67600' }
          }
        };

      });

      /* ============================================
         DATA ROW STYLING
      ============================================ */

      worksheet.eachRow((row, rowNumber) => {

        if (rowNumber === 1) {
          return;
        }

        row.eachCell((cell) => {

          cell.alignment = {
            vertical: 'top',
            wrapText: true
          };

          // Alternate row background
          if (rowNumber % 2 === 0) {

            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFF5E8' }
            };

          }

        });

      });

      /* ============================================
         PRICE FORMAT
      ============================================ */

      worksheet.getColumn('price').numFmt = '#,##0.00';

      /* ============================================
         FILTERS
      ============================================ */

      worksheet.autoFilter = {
        from: {
          row: 1,
          column: 1
        },
        to: {
          row: 1,
          column: columns.length
        }
      };

      /* ============================================
         FREEZE HEADER
      ============================================ */

      worksheet.views = [
        {
          state: 'frozen',
          ySplit: 1
        }
      ];

    };

    /* ============================================
       SEPARATE SHORT & LONG COURSES
    ============================================ */

    const shortCourses = courses.filter(
      course => course.courseType?.toUpperCase() === 'SHORT'
    );

    const qualifications = courses.filter(
      course => course.courseType?.toUpperCase() === 'LONG'
    );

    /* ============================================
       CREATE SHEETS BASED ON ACTION
    ============================================ */

    if (this.downloadCourseType === 'ALL') {

      // Sheet 1: Short Courses
      createWorksheet('Short Courses', shortCourses);

      // Sheet 2: Qualifications
      createWorksheet('Qualifications', qualifications);

    }
    else if (this.downloadCourseType === 'SHORT') {

      createWorksheet('Short Courses', shortCourses);

    }
    else if (this.downloadCourseType === 'LONG') {

      createWorksheet('Qualifications', qualifications);

    }

    /* ============================================
       GENERATE EXCEL FILE
    ============================================ */

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob(
      [buffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    );

    // File name
    const typeName = this.downloadCourseType === 'LONG'
      ? 'Qualifications'
      : this.downloadCourseType === 'SHORT'
        ? 'Short_Courses'
        : 'All_Courses';

    const fileName =
      `ECB_${typeName}_${this.getExcelDate()}.xlsx`;

    // Download file
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);

  }



  /* ============================================
     CONVERT HTML DESCRIPTION TO PLAIN TEXT
  ============================================ */

  private getPlainExcelText(
    html: string | null | undefined
  ): string {

    if (!html) {
      return '';
    }

    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    doc.querySelectorAll('script, style').forEach(
      element => element.remove()
    );

    return doc.body.textContent?.trim() || '';
  }


  /* ============================================
     EXCEL FILE DATE
  ============================================ */

  private getExcelDate(): string {

    const date = new Date();

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}${month}${day}`;

  }



}
